import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { markFasonAtPrinting, markFasonReceived } from "../api/fasonOrders";

/**
 * FasonStatusModal
 * mode: "at_printing" | "received"
 * fasonOrder: the fason order object
 */
function FasonStatusModal({ fasonOrder, mode, onClose, onSaved }) {
  const isAtPrinting = mode === "at_printing";

  const [qty, setQty] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const platesPerSheet = Number(fasonOrder?.plates_per_sheet || 0);
  const qtyNum = Number(qty);
  const calculatedPlates =
    isAtPrinting && platesPerSheet > 0 && qtyNum > 0
      ? Math.round(qtyNum * platesPerSheet)
      : null;

  async function save() {
    setError("");
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) {
      setError("Miktar > 0 olmalı");
      return;
    }

    setSaving(true);
    try {
      if (isAtPrinting) {
        await markFasonAtPrinting(fasonOrder.id, {
          qty_at_printing: q,
          arrived_at_printing_date: date,
        });
      } else {
        await markFasonReceived(fasonOrder.id, {
          qty_received: q,
          received_date: date,
        });
      }
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("FasonStatusModal save error:", err);
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "İşlem gerçekleştirilemedi";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  const title = isAtPrinting ? "Baskıya Ulaştı" : "Teslim Alındı";
  const outputMatName = fasonOrder?.output_material_name || fasonOrder?.output_material_code || "";
  const sheetWidth = fasonOrder?.sheet_width;
  const sheetHeight = fasonOrder?.sheet_height;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {isAtPrinting ? (
            <Typography variant="body2" color="text.secondary">
              Hammadde Tabaka ({fasonOrder?.input_material_name || ""}) stoğa alınacak.
              {sheetWidth && sheetHeight ? (
                <> Tabaka ölçüsü: <strong>{sheetWidth} × {sheetHeight} cm</strong>.</>
              ) : null}
              <br />
              Varyant bilgisi iş emri ve en/boy ölçülerinden otomatik belirlenir.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Hammadde ({fasonOrder?.input_material_name || ""},{" "}
              {fasonOrder?.qty_at_printing ?? 0} tabaka) stoktan düşecek.
              <br />
              Yarımamül ({outputMatName}) stoğa girecek.
              {platesPerSheet > 0 && (
                <> Bıçak: <strong>{platesPerSheet} tabak/tabaka</strong>.</>
              )}
              <br />
              Varyant bilgisi iş emrinden otomatik belirlenir.
            </Typography>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label={isAtPrinting ? "Baskıya Giden Miktar (Tabaka)" : "Teslim Alınan Miktar (Tabaka)"}
            type="number" inputProps={{ min: 1 }}
            value={qty} onChange={(e) => setQty(e.target.value)}
            required disabled={saving} autoFocus
            helperText={
              calculatedPlates != null
                ? `${qty} tabaka × ${platesPerSheet} = ${calculatedPlates.toLocaleString("tr-TR")} tabak`
                : isAtPrinting && platesPerSheet > 0
                ? `Bıçak: ${platesPerSheet} tabak/tabaka`
                : undefined
            }
          />

          <TextField
            label={isAtPrinting ? "Baskıya Varış Tarihi" : "Teslim Alış Tarihi"}
            type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} disabled={saving}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>İptal</Button>
        <Button
          onClick={save}
          variant="contained"
          color={isAtPrinting ? "primary" : "success"}
          disabled={saving}
        >
          {saving ? "İşleniyor..." : title}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FasonStatusModal;
